import React from 'react'
import Markup from 'markup/ui/markup'
import MarkupService from 'markup/application/MarkupService'

import eblteam2020 from 'about/ui/static/eblteam2020.jpg'
import eblteam2023 from 'about/ui/static/eblteam2023.jpg'

import 'about/ui/project.sass'

export default function AboutProject(
  markupService: MarkupService,
): JSX.Element {
  return (
    <div className="project-page">
      <header className="project-hero">
        <p className="project-hero__eyebrow">
          Electronic Babylonian Literature
        </p>
        <h2 className="project-hero__title">eBL Project</h2>
        <p className="project-hero__lead">
          Reconstructing Babylonian literature and making fragmentary texts
          searchable, accessible, and usable for scholarship.
        </p>
      </header>

      <section className="project-section">
        <div className="project-section__header">
          <h3>History of the Project</h3>
          <p className="project-section__subtitle">
            From fragmentary discovery to reconstructed literature.
          </p>
        </div>

        <div className="project-intro">
          <div className="project-intro__text">
            <div className="gilgamesh-quote">
              <div className="gilgamesh-quote__fragmentary">
                <span className="gilgamesh-quote__line">
                  [… who s]aw the Deep, […] the country,
                </span>
                <span className="gilgamesh-quote__line gilgamesh-quote__parallel">
                  [who] knew […], […] all […]
                </span>
                <span className="gilgamesh-quote__line">
                  [… who] saw the Deep, […] the country,
                </span>
                <span className="gilgamesh-quote__line gilgamesh-quote__parallel">
                  [who] knew […], […] all […]
                </span>
              </div>
              <div className="gilgamesh-quote__restored">
                <span className="gilgamesh-quote__line">
                  He who saw the Deep, the foundation of the country,
                </span>
                <span className="gilgamesh-quote__line gilgamesh-quote__parallel">
                  who knew the proper ways, was wise in all matters!
                </span>
                <span className="gilgamesh-quote__line">
                  Gilgamesh, who saw the Deep, the foundation of the country,
                </span>
                <span className="gilgamesh-quote__line gilgamesh-quote__parallel">
                  who knew the proper ways, was wise in all matters!
                </span>
              </div>
            </div>

            <div className="project-description">
              <Markup
                markupService={markupService}
                text="The first quote represents the beginning of the Epic of Gilgamesh as known from the 19th century onwards (@bib{RN2484@14}). The second shows the text fully restored, in the form it achieved over one hundred years later, in 2007 (see @bib{RN271}). Throughout the twentieth century, therefore, only the fragmentary version of the prologue of the Epic was known: generations of readers, when first confronted with the foremost classic of ancient Mesopotamian literature, experienced the frustration of reading a fragmentary text, of being allowed merely a latticed glimpse into the world of the Babylonians. This frustration is every cuneiform scholar's bread and butter, often to be consumed 'when one struggles with a fragmentary text in the Students' Room of the British Museum and suspects with more or less reason that unidentified pieces are lying in drawers just a few meters away' (@bib{RN51@41–42}). It is precisely against this frustration that the Electronic Babylonian Literature Project has declared war."
              />
            </div>
            <div className="project-description">
              <Markup
                markupService={markupService}
                text="The Electronic Babylonian Literature (eBL) Project started in April 2018 at Ludwig Maximilian University of Munich thanks to the generous support of a Sofja Kovalevskaja Award from the Alexander von Humboldt Fundation. The goal of the project is to bring Babylonian literature to the point of what can currently be reconstructed. Moreover, it aims to make accessible a large mass of transliterations of fragments of cuneiform tablets and a tool to allow scholars to search it quickly, thus providing a lasting solution to the abiding problem of the fragmentary character of Mesopotamian Literature."
              />
            </div>
          </div>
        </div>
      </section>

      <section className="project-section">
        <div className="project-section__header">
          <h3>List of Participants</h3>
          <p className="project-section__subtitle">
            The people and partners behind the project.
          </p>
        </div>

        <div className="team-photos">
          <figure className="team-photos__item">
            <img src={eblteam2020} alt="The eBL Team in 2020" />
            <figcaption>The eBL Team in 2020</figcaption>
          </figure>
          <figure className="team-photos__item">
            <img src={eblteam2023} alt="The eBL Team in 2023" />
            <figcaption>The eBL Team in 2023</figcaption>
          </figure>
        </div>

        <div className="team-section">
          <div className="team-section__grid">
            <div className="team-section__card">
              <h4>Project&apos;s Staff (Cuneiformists)</h4>
              <ul>
                <li>Enrique Jiménez, Principal Investigator</li>
                <li>Zsombor Földi, Editor</li>
                <li>Aino Hätinen, Editor</li>
                <li>Adrian Heinrich, Editor (until 03.2022)</li>
                <li>Tonio Mitto, Editor (PhD student, until 10.2022)</li>
                <li>Geraldina Rozzi, Editor (since 01.2021)</li>
              </ul>
            </div>

            <div className="team-section__card">
              <h4>Project&apos;s Staff (Developers)</h4>
              <ul>
                <li>Ilya Khait (since 03.2022)</li>
                <li>Jussi Laasonen (until 04.2022)</li>
                <li>Fabian Simonjetz (since 04.2022)</li>
              </ul>
            </div>

            <div className="team-section__card">
              <h4>Student Assistants</h4>
              <ul>
                <li>Yunus Cobanoglu (Computer Science)</li>
                <li>Cyril Dankwardt</li>
                <li>Ekaterine Gogokhia</li>
                <li>Louisa Grill</li>
                <li>Wasim Khatabe</li>
                <li>Alexander Kudriavtcev (until 2021)</li>
                <li>Daniel López-Kuczmik</li>
                <li>Mays Al-Rawi</li>
                <li>Wadieh Zerkly</li>
              </ul>
            </div>

            <div className="team-section__card">
              <h4>External Collaborators</h4>
              <ul>
                <li>Anmar A. Fadhil (University of Baghdad)</li>
                <li>Benjamin R. Foster (Yale University)</li>
                <li>Alberto Giannese (British Museum)</li>
                <li>Carmen Gütschow</li>
                <li>Ivor Kerslake (British Museum)</li>
                <li>Felix Müller (Universität Göttingen)</li>
                <li>Jeremiah Peterson</li>
                <li>Luis Sáenz (Universität Heidelberg)</li>
                <li>Henry Stadhouders</li>
                <li>Junko Taniguchi</li>
                <li>Abraham Winitzer (University of Notre Dame)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="project-section">
        <div className="project-section__header">
          <h3>Participating Institutions</h3>
          <p className="project-section__subtitle">
            Institutional partners supporting research and access.
          </p>
        </div>
        <div className="institutions">
          <ul>
            <li>
              <a href="http://www.britishmuseum.org/">The British Museum</a>,
              London. A particular debt of gratitude is owed to Dr. Jonathan
              Taylor, Assistant Keeper (Department of the Middle East) for his
              continued support of the project. Photographs of cuneiform tablets
              kept in the British Museum are published online with the kind
              permission of the Trustees of the British Museum.
            </li>
            <li>
              <a href="http://www.penn.museum/">
                University of Pennsylvania Museum of Archaeology and
                Anthropology
              </a>
              . Thanks are expressed to Prof. Stephen J. Tinney, Deputy Director
              and Chief Curator.
            </li>
            <li>
              <a href="http://nelc.yale.edu/babylonian-collection">
                Yale Babylonian Collection
              </a>
              , Yale University. Thanks are expressed to Dr. Agnete W. Lassen,
              Associate Curator; to Prof. Eckart Frahm; and to Dr. Klaus
              Wagensonner.
            </li>
          </ul>
        </div>
      </section>
    </div>
  )
}
